/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CatalogDashboard } from "@app/api/trpc/routers/catalog";
import { money } from "./format";

type Props = { products: CatalogDashboard["products"]; total: number };

const LIMIT = 8;

// Top sellers by sales, with their share and margin when they have a recipe.
export const BestSellers = defineComponent<Props>({
  name: "BestSellers",
  props: ["products", "total"],
  setup(props) {
    const { t } = useLocalization();

    return () => {
      if (!props.products.length) {
        return <p class="text-sm text-base-content/50">{t("catalog.overview.no_sales")}</p>;
      }
      return (
        <div class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr class="text-xs text-base-content/50">
                <th class="px-0">{t("catalog.overview.col_product")}</th>
                <th class="text-right">{t("catalog.overview.col_sold")}</th>
                <th class="text-right">{t("catalog.overview.col_sales")}</th>
                <th class="hidden w-32 sm:table-cell">{t("catalog.overview.col_share")}</th>
                <th class="px-0 text-right">{t("catalog.overview.col_margin")}</th>
              </tr>
            </thead>
            <tbody>
              {props.products.slice(0, LIMIT).map((p, i) => {
                const share = props.total > 0 ? (p.revenue / props.total) * 100 : 0;
                const marginPct =
                  p.unit_cost != null && p.revenue_net > 0
                    ? ((p.revenue_net - p.unit_cost * p.quantity) / p.revenue_net) * 100
                    : null;
                return (
                  <tr key={p.id}>
                    <td class="px-0">
                      <div class="flex items-center gap-2">
                        <span class="w-4 text-xs tabular-nums text-base-content/40">{i + 1}</span>
                        <div class="min-w-0">
                          <div class="truncate font-medium text-accent">{p.name}</div>
                          {p.category_name && <div class="truncate text-xs text-base-content/50">{p.category_name}</div>}
                        </div>
                      </div>
                    </td>
                    <td class="text-right tabular-nums">{p.quantity}</td>
                    <td class="text-right tabular-nums text-accent">{money(p.revenue)}</td>
                    <td class="hidden sm:table-cell">
                      <div class="flex items-center gap-2">
                        <div class="h-1.5 flex-1 overflow-hidden rounded-full bg-base-200">
                          <div class="h-full rounded-full bg-primary" style={{ width: `${Math.min(share, 100)}%` }} />
                        </div>
                        <span class="w-9 text-right text-xs tabular-nums text-base-content/50">{`${share.toFixed(0)}%`}</span>
                      </div>
                    </td>
                    <td class="px-0 text-right tabular-nums">
                      {marginPct == null ? (
                        <span class="text-base-content/30" title={t("catalog.overview.no_recipe_hint")}>—</span>
                      ) : (
                        <span class={marginPct < 60 ? "text-warning" : ""}>{`${marginPct.toFixed(0)}%`}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    };
  },
});
